<template>
  <div class="wx-card">
    <!-- Store weather signature -->
    <div class="wx-sig">
      <div class="wx-sig-icon"><i class="bi bi-cloud-sun"></i></div>
      <div class="wx-sig-body">
        <div class="wx-sig-headline">{{ signature.headline }}</div>
        <div class="wx-drivers">
          <span v-for="d in signature.drivers" :key="d.label"
                class="wx-driver" :class="d.dir === 'up' ? 'up' : 'down'">
            <i :class="d.icon"></i>
            {{ d.label }}
            <b>{{ d.dir === 'up' ? '+' : '−' }}{{ d.pct }}%</b>
            <i class="bi" :class="d.dir === 'up' ? 'bi-arrow-up-short' : 'bi-arrow-down-short'"></i>
          </span>
        </div>
        <div class="wx-sig-note">{{ signature.note }}</div>
      </div>
    </div>

    <div class="wx-divider"></div>

    <!-- Per-zone weather signals (the Discover engine) -->
    <div class="wx-zones-head">
      <span>{{ $t('demo.weather.byZone') }}</span>
      <span class="wx-legend">
        <i class="bi bi-star-fill"></i> {{ $t('demo.weather.confirmed') }}
        <i class="bi bi-eye ms-2"></i> {{ $t('demo.weather.earlySignal') }}
      </span>
    </div>

    <ul class="wx-list">
      <li v-for="z in zoneSignals" :key="z.zone" class="wx-row" :class="{ muted: z.band === 'none' }">
        <div class="wx-row-icon" :class="z.dir"><i :class="z.icon"></i></div>
        <div class="wx-row-main">
          <div class="wx-row-top">
            <span class="wx-zone">{{ z.zone }}</span>
            <span v-if="z.kind === 'anticipatory'" class="wx-tag antic">
              <i class="bi bi-clock-history"></i> {{ $t('demo.weather.anticipatory') }}
            </span>
          </div>
          <div class="wx-phrase">{{ z.phrase }}</div>
        </div>
        <div class="wx-row-right">
          <span v-if="z.band !== 'none'" class="wx-effect" :class="z.dir">
            {{ z.dir === 'up' ? '+' : '−' }}{{ z.pct }}%
          </span>
          <span class="wx-band" :class="z.band" :title="bandTip(z.band)">
            <i class="bi" :class="z.band === 'established' ? 'bi-star-fill' : (z.band === 'watch' ? 'bi-eye' : 'bi-dash-circle')"></i>
            {{ bandLabel(z.band) }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
// Demo-only, hard-coded to mirror what the real weather-response engine emits:
// a store weather signature (a driver + confidence) and per-zone signal atoms
// (zone, plain-language phrase, effect %, direction, confidence band, timing).
// Sample sentences carry their Dutch (withNl): read in the current language.
import { useI18n } from 'vue-i18n'
import { withNl } from '@/demo/demoLang'
const { t } = useI18n()
const signature = withNl({
  headline: 'Newell DIY runs a little busier in warm, dry weather.',
  drivers: [
    withNl({ label: 'Warmer', dir: 'up', pct: 9, icon: 'bi-thermometer-sun' }, { label: 'Warmer' }),
    withNl({ label: 'Rain', dir: 'down', pct: 6, icon: 'bi-cloud-rain' }, { label: 'Regen' }),
  ],
  note: 'Store-wide the effect is mild — the real story is per zone.',
}, {
  headline: 'Newell DIY is iets drukker bij warm, droog weer.',
  note: 'Voor de hele winkel is het effect klein. Het echte verhaal zit per zone.',
})

const zoneSignals = [
  withNl({
    zone: 'Garden Utilities', dir: 'up', pct: 28, band: 'watch', kind: 'anticipatory',
    icon: 'bi-sun',
    phrase: 'Busier 1–2 days before a warm, dry spell — shoppers plan garden jobs around the forecast.',
  }, { phrase: 'Drukker 1–2 dagen voor een warme, droge periode: bezoekers plannen tuinwerk rond de weersverwachting.' }),
  withNl({
    zone: 'Power Tools', dir: 'up', pct: 18, band: 'established', kind: 'same-day',
    icon: 'bi-cloud-rain',
    phrase: 'Draws a bigger share of shoppers on wet days — indoor projects when the garden’s off.',
  }, { phrase: 'Trekt op natte dagen een groter deel van je bezoekers: klussen binnen als de tuin niet kan.' }),
  withNl({
    zone: 'Promoted Articles', dir: 'down', pct: 11, band: 'established', kind: 'same-day',
    icon: 'bi-thermometer-sun',
    phrase: 'Loses share in the heat — fewer shoppers linger at the front on hot afternoons.',
  }, { phrase: 'Verliest aandeel in de hitte: op warme middagen blijven minder bezoekers vooraan staan.' }),
  withNl({
    zone: 'Safety Shoes', dir: 'flat', pct: 0, band: 'none', kind: null,
    icon: 'bi-dash-lg',
    phrase: 'No clear weather link — steady whatever the sky.',
  }, { phrase: 'Geen duidelijk verband met het weer: stabiel, wat de lucht ook doet.' }),
]

function bandLabel(b) {
  return b === 'established' ? t('demo.weather.confirmed') : (b === 'watch' ? t('demo.weather.earlySignal') : t('demo.weather.noLink'))
}
function bandTip(b) {
  return b === 'established' ? t('demo.weather.tipConfirmed')
    : (b === 'watch' ? t('demo.weather.tipEarly')
      : t('demo.weather.tipNone'))
}
</script>

<style scoped>
.wx-card {
  background: #fff;
  border: 1px solid #e8ebe0;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(20, 30, 10, 0.04);
}

/* signature */
.wx-sig { display: flex; gap: 16px; align-items: flex-start; }
.wx-sig-icon {
  flex: 0 0 auto; width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #eaf1ff, #f2f7ec);
  color: #5b7fb0; font-size: 22px;
}
.wx-sig-headline { font-weight: 650; font-size: 1.02rem; color: #2a3320; }
.wx-drivers { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0 4px; }
.wx-driver {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.82rem; padding: 3px 10px; border-radius: 999px;
  background: #f4f6ef; color: #45503a;
}
.wx-driver b { font-weight: 700; }
.wx-driver.up { color: #3f6b2f; }
.wx-driver.down { color: #9a6a1c; }
.wx-sig-note { font-size: 0.8rem; color: #8a9280; margin-top: 2px; }

.wx-divider { height: 1px; background: #eef1e8; margin: 16px 0 12px; }

.wx-zones-head {
  display: flex; justify-content: space-between; align-items: baseline;
  flex-wrap: wrap; gap: 2px 10px;
  font-weight: 650; color: #2a3320; font-size: 0.92rem; margin-bottom: 6px;
}
.wx-legend { font-size: 0.74rem; color: #9aa28e; font-weight: 500; }
.wx-legend .bi-star-fill { color: #B2C149; }

/* zone rows */
.wx-list { list-style: none; margin: 0; padding: 0; }
.wx-row {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 6px; border-bottom: 1px solid #f4f6ef;
}
.wx-row:last-child { border-bottom: none; }
.wx-row.muted { opacity: 0.6; }
.wx-row-icon {
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center; font-size: 16px;
  background: #f2f5ec; color: #6f7a5c;
}
.wx-row-icon.up { background: #eef6e8; color: #4f8f3a; }
.wx-row-icon.down { background: #fdf3e3; color: #c08a2e; }
.wx-row-main { flex: 1 1 auto; min-width: 0; }
.wx-row-top { display: flex; align-items: center; gap: 8px; }
.wx-zone { font-weight: 600; color: #2a3320; }
.wx-tag {
  font-size: 0.68rem; padding: 2px 7px; border-radius: 999px;
  background: #eef2fb; color: #4f6b9c; display: inline-flex; align-items: center; gap: 3px;
}
.wx-phrase { font-size: 0.83rem; color: #77806a; margin-top: 2px; }
.wx-row-right { flex: 0 0 auto; display: flex; align-items: center; gap: 10px; }
.wx-effect { font-weight: 700; font-size: 0.95rem; }
.wx-effect.up { color: #4f8f3a; }
.wx-effect.down { color: #c08a2e; }
.wx-band {
  font-size: 0.72rem; padding: 3px 9px; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
}
.wx-band.established { background: #eef6e8; color: #3f6b2f; }
.wx-band.watch { background: #f3f4f6; color: #6b7280; }
.wx-band.none { background: #f3f4f6; color: #9aa28e; }

@media (max-width: 720px) {
  .wx-row-right { flex-direction: column; align-items: flex-end; gap: 2px; }
}
</style>
